import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Text, Input, Button, Icon, Tooltip } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import api from '../../utils/axios';
import { getCABFileInfo, downloadCABMarkdown, getCABMarkdownContent } from '../../services/cabApi';
import type { Document, DocumentsPanelProps, DocumentTab, FileTypeFilter } from './DocumentsPanel.types';
import { getFileExtension, getFileTypeCategory, formatFileSize } from '../../utils/fileUtils';
import { formatDate } from '../../utils/dateUtils';
import { formatArtifactTitle } from '../../utils/parseArtifacts';
import { FileTypeIcon } from './components/FileTypeIcon';
import ArtifactDetail from '../ArtifactDetail';
import { DragAndDropHandler } from '../DragAndDrop';
import { 
  SUPPORTED_FILE_EXTENSIONS,
  FILE_INPUT_ACCEPT,
  isSupportedExtension,
  SUPPORTED_ALL_FORMATS,
  MAX_FILENAME_LENGTH,
  isImageFile,
  isImageExtension
} from '../../utils/fileUploadConfig';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';

// Skeleton Loading Component for Document Rows
const DocumentRowSkeleton: React.FC<{ index: number }> = ({ index }) => {
  return (
    <tr 
      className="border-b border-border-secondary animate-fade-in-up opacity-0"
      style={{ 
        animationDelay: `${index * 0.05}s`,
        animationFillMode: 'forwards'
      }}
    >
      {/* Name Column */}
      <td className="py-bg-v2-md px-bg-v2-lg">
        <div className="flex items-center gap-bg-v2-md">
          {/* File Icon Skeleton */}
          <div className="w-8 h-8 skeleton-shimmer rounded-bg-v2-sm shrink-0"></div>
          
          {/* File Info Skeleton */}
          <div className="flex flex-col gap-bg-v2-xs flex-1 min-w-0">
            <div className="h-5 skeleton-shimmer rounded-bg-v2-sm w-3/5"></div>
            <div className="h-4 skeleton-shimmer rounded-bg-v2-sm w-1/4"></div>
          </div>
        </div>
      </td>

      {/* Date Column */}
      <td className="py-bg-v2-md px-bg-v2-lg">
        <div className="flex items-center justify-center">
          <div className="h-5 skeleton-shimmer rounded-bg-v2-sm w-32"></div>
        </div>
      </td>

      {/* Type Column */}
      <td className="py-bg-v2-md px-bg-v2-lg">
        <div className="flex items-center justify-center">
          <div className="h-6 skeleton-shimmer rounded-bg-v2-full w-16"></div>
        </div>
      </td>

      {/* Action Column */}
      <td className="py-bg-v2-md px-bg-v2-lg">
        <div className="flex items-center justify-center gap-bg-v2-lg">
          <div className="w-4 h-4 skeleton-shimmer rounded-bg-v2-sm shrink-0"></div>
          <div className="w-4 h-4 skeleton-shimmer rounded-bg-v2-sm shrink-0"></div>
          <div className="w-4 h-4 skeleton-shimmer rounded-bg-v2-sm shrink-0"></div>
        </div>
      </td>
    </tr>
  );
};

const DocumentsPanel: React.FC<DocumentsPanelProps> = ({
  project,
  onClose,
  onDocumentSelect,
}) => {
  const [activeTab, setActiveTab] = useState<DocumentTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<Set<Exclude<FileTypeFilter, 'all'>>>(new Set());
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'type'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const { projectId } = useParams();

  // Check if user has update permission
  const hasUpdatePermission = project?.permissions?.includes(Permissions.UpdateProjectPermission) || false;

  // Load documents (artifacts + files) in parallel
  useEffect(() => {
    loadDocuments();
  }, [projectId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      // Call all APIs in parallel (CAB status is non-blocking — failure is ignored)
      const [artifactsResponse, filesResponse, imagesResponse, cabStatus] = await Promise.all([
        api.get(`/projects/${projectId}/artifacts/`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        api.get(`/projects/${projectId}/files/`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        api.get(`/projects/${projectId}/images/`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        projectId ? getCABFileInfo(projectId).catch(() => null) : Promise.resolve(null),
      ]);

      // Helper to ensure we always have a valid ISO string for dateModified
      const ensureISO = (val: any) => {
        if (!val) return new Date().toISOString();
        // Preserve existing strings as-is to avoid timezone shifts during re-parsing.
        // For strings: We pass them through exactly as they are. Previously, we were doing 
        // new Date(val).toISOString(). If the backend sent a string like 12:00 without a Z, 
        // the browser would treat it as 12:00 Local, and toISOString() would convert it to 
        // 06:30 UTC (if you are in GMT+5:30). This was the "wrong time" seen previously.
        if (typeof val === 'number') return new Date(val * 1000).toISOString();
        return val;
      };

      // Transform artifacts to Document format (System Generated)
      const artifactDocs: Document[] = (artifactsResponse.data || []).map((artifact: any) => ({
        id: artifact.id || artifact._id,
        name: artifact.title || 'Untitled',
        size: artifact.size || artifact.content?.length || 0,
        dateModified: ensureISO(artifact.updated_at || artifact.created_at),
        type: 'system' as const,
        fileType: artifact.type || getFileExtension(artifact.title || 'document.txt'),
        content: artifact.content,
        version: artifact.version,
        source: 'artifact',
      }));

      // Transform files to Document format (User Uploaded)
      const fileDocs: Document[] = (filesResponse.data || []).map((file: any) => ({
        id: file.id || file._id,
        name: file.original_name || file.filename || 'Unknown File',
        size: file.size || 0,
        dateModified: ensureISO(file.uploaded_at || file.created_at || file.uploadedAt),
        type: 'user' as const,
        fileType: getFileExtension(file.original_name || file.filename || ''),
        source: 'file',
      }));

       // Transform files to Document format (User Uploaded)
       const images: Document[] = (imagesResponse.data || []).map((file: any) => ({
        id: file.id || file._id,
        name: file.original_name || file.filename || 'Unknown File',
        size: file.size || 0,
        dateModified: ensureISO(file.uploaded_at || file.created_at || file.uploadedAt),
        type: 'user' as const,
        fileType: getFileExtension(file.original_name || file.filename || ''),
        source: 'image',
      }));

      // Add CAB.md as a system document if it is ready
      const cabDocs: Document[] = [];
      if (cabStatus?.is_ready) {
        const cabDate = ensureISO(cabStatus.completed_at);

        cabDocs.push({
          id: `cab_markdown_${projectId}`,
          name: 'CAB.md',
          size: cabStatus.cab_md_size || 0,
          dateModified: cabDate,
          type: 'system',
          fileType: 'md',
          source: 'cab_markdown',
        });
      }

      // Combine and set documents
      setDocuments([...artifactDocs, ...fileDocs, ...images, ...cabDocs]);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter documents based on active tab, search, and file type filter
  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    // Filter by tab
    if (activeTab === 'user') {
      filtered = filtered.filter(doc => doc.type === 'user');
    } else if (activeTab === 'system') {
      filtered = filtered.filter(doc => doc.type === 'system');
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.name.toLowerCase().includes(query)
      );
    }

    // Filter by file type
    if (selectedFilters.size > 0) {
      filtered = filtered.filter(doc => {
        const category = getFileTypeCategory(doc.fileType);
        return Array.from(selectedFilters).some(filter => {
          if (filter === 'word') return category === 'word';
          if (filter === 'excel') return category === 'excel';
          if (filter === 'powerpoint') return category === 'powerpoint';
          if (filter === 'pdf') return category === 'pdf';
          if (filter === 'images') return category === 'image';
          if (filter === 'others') return !['word', 'excel', 'powerpoint', 'pdf', 'image'].includes(category);
          return false;
        });
      });
    }

    // 2. Sort documents (using a copy to avoid mutating state)
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortBy === 'date') {
        // Robust date parsing for sort: matches formatDate utility logic
        const parse = (val: string | number) => {
          if (!val) return 0;
          if (typeof val === 'number') return val * 1000;
          
          let dateString = val;
          // If the string doesn't have timezone info, treat it as UTC (matching dateUtils.ts)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/) && !dateString.match(/Z|[+-]\d{2}:\d{2}$/)) {
            dateString = dateString.replace(/(\.\d+)?$/, '') + 'Z';
          }
          const d = new Date(dateString);
          return isNaN(d.getTime()) ? 0 : d.getTime();
        };

        const timeA = parse(a.dateModified);
        const timeB = parse(b.dateModified);
        comparison = timeA - timeB;
      } else if (sortBy === 'type') {
        comparison = a.type.localeCompare(b.type);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [documents, activeTab, searchQuery, selectedFilters, sortBy, sortOrder]);

  const handleSort = (column: 'name' | 'date' | 'type') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const renderTableHeader = (theadClassName?: string) => {
    return (
      <>
        <colgroup>
          <col style={{ width: '40%' }} />
          <col style={{ width: '25%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead className={theadClassName}>
          <tr className="font-bg-v2-body text-text-tertiary text-bg-v2-sm border-b border-border-secondary">
            <th className="text-left py-bg-v2-md px-bg-v2-lg">
              <div className="flex items-center gap-bg-v2-xs cursor-pointer" onClick={() => handleSort('name')}>
                <Text variant="text-sm" weight="medium" color="tertiary">Name</Text>
                <Icon name={IconName.Sort} size="sm" />
              </div>
            </th>
            <th className="py-bg-v2-md px-bg-v2-lg">
              <div className="flex items-center justify-center gap-bg-v2-xs cursor-pointer" onClick={() => handleSort('date')}>
                <Text variant="text-sm" weight="medium" color="tertiary">Date Modified</Text>
                <Icon name={IconName.Sort} size="sm" />
              </div>
            </th>
            <th className="py-bg-v2-md px-bg-v2-lg">
              <div className="flex items-center justify-center gap-bg-v2-xs cursor-pointer" onClick={() => handleSort('type')}>
                <Text variant="text-sm" weight="medium" color="tertiary">Type</Text>
                <Icon name={IconName.Sort} size="sm" />
              </div>
            </th>
            <th className="py-bg-v2-md px-bg-v2-lg">
              <div className="flex items-center justify-center">
                <Text variant="text-sm" weight="medium" color="tertiary">Action</Text>
              </div>
            </th>
          </tr>
        </thead>
      </>
    );
  };

  const renderNoDocumentsFound = () => {
    return (
      <div className="flex flex-col h-full">
        {/* Header section with fixed height */}
        <div className="flex-none sticky top-0 bg-bg-primary z-10">
          <table className="w-full table-fixed">
            {renderTableHeader()}
          </table>
        </div>
        
        {/* Content area that takes remaining space */}
        <div className="flex-1 flex items-center justify-center">
          <Text variant="text-sm" color="tertiary">No files found</Text>
        </div>
      </div>
    );
  };

  // Extract upload logic into reusable function
  const processUploadFiles = async (files: File[]) => {

    if (uploading) {
      return; // Prevent concurrent uploads
    }

    // Filter files by accepted types
    const validFiles = files.filter(file => isSupportedExtension(file.name));
    const invalidFiles = files.filter(file => !isSupportedExtension(file.name));

    if (validFiles.length === 0) {
      if (invalidFiles.length > 0) {
        const unsupportedFiles = invalidFiles.map(f => f.name).join(', ');
        alert(`❌ Unsupported file format!\n\nThe following files are not supported:\n${unsupportedFiles}\n\nSupported formats: ${SUPPORTED_ALL_FORMATS}`);
      }
      return;
    }

    // Show warning if some files are invalid (unsupported extension)
    if (invalidFiles.length > 0) {
      const unsupportedFiles = invalidFiles.map(f => f.name).join(', ');
      alert(`⚠️ Some files were skipped:\n${unsupportedFiles}\n\nOnly supported formats will be uploaded.`);
    }

    // ── Pre-flight: check filename length before any API call ──
    const tooLongFiles = validFiles.filter(f => f.name.length > MAX_FILENAME_LENGTH);
    if (tooLongFiles.length > 0) {
      const details = tooLongFiles.map(f => `• "${f.name.slice(0, 50)}…" (${f.name.length} chars)`).join('\n');
      alert(`❌ Filename too long!\n\nThe following files have names exceeding the ${MAX_FILENAME_LENGTH}-character limit:\n${details}\n\nPlease rename them and try again.`);
      return;
    }

    // Separate images from documents
    const imageFiles = validFiles.filter(file => isImageFile(file) || isImageExtension(file.name));
    const documentFiles = validFiles.filter(file => !imageFiles.includes(file));

    setUploading(true);
    setUploadProgress({ current: 0, total: validFiles.length });
    
    let successCount = 0;
    let failCount = 0;
    type FailedFile = { name: string; reason: string };
    const failedFiles: FailedFile[] = [];
    
    try {
      let currentIndex = 0;
      
      // Upload images to /images/ endpoint
      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        try {
          currentIndex++;
          setUploadProgress({ current: currentIndex, total: validFiles.length });
          
          const formData = new FormData();
          formData.append('file', file);
          
          await api.post(`/projects/${projectId}/images/`, formData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          successCount++;
        } catch (error: any) {
          console.error(`Failed to upload image ${file.name}:`, error);
          const reason = error?.response?.data?.detail ?? 'Upload failed';
          failedFiles.push({ name: file.name, reason });
          failCount++;
        }
      }
      
      // Upload documents to /files/ endpoint
      for (let i = 0; i < documentFiles.length; i++) {
        const file = documentFiles[i];
        try {
          currentIndex++;
          setUploadProgress({ current: currentIndex, total: validFiles.length });
          
          const formData = new FormData();
          formData.append('file', file);
          
          await api.post(`/projects/${projectId}/files/`, formData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          successCount++;
        } catch (error: any) {
          console.error(`Failed to upload document ${file.name}:`, error);
          const reason = error?.response?.data?.detail ?? 'Upload failed';
          failedFiles.push({ name: file.name, reason });
          failCount++;
        }
      }
      
      // Reload documents after upload attempts
      await loadDocuments();
      
      // Show result message
      if (failCount === 0) {
        alert(`✅ Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}!`);
      } else if (successCount === 0) {
        const details = failedFiles.map(f => `• ${f.name}: ${f.reason}`).join('\n');
        alert(`❌ Failed to upload all ${failCount} file${failCount > 1 ? 's' : ''}:\n\n${details}`);
      } else {
        const details = failedFiles.map(f => `• ${f.name}: ${f.reason}`).join('\n');
        alert(`⚠️ Uploaded ${successCount} file${successCount > 1 ? 's' : ''} successfully. ${failCount} file${failCount > 1 ? 's' : ''} failed:\n\n${details}`);
      }
    } catch (error) {
      console.error('File upload process failed:', error);
      alert('❌ Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const handleUploadFiles = () => {

    // Create a hidden file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.accept = FILE_INPUT_ACCEPT; // Allow images and documents
    
    fileInput.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const files = target.files;
      
      if (!files || files.length === 0) return;
      
      const fileArray = Array.from(files);
      await processUploadFiles(fileArray);
    };
    
    // Trigger file selection dialog
    fileInput.click();
  };

  // Handle drag enter - check if files are being dragged
  const handleDragEnter = (e: React.DragEvent) => {
    // Check if files are being dragged
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingFiles(true);
    } else {
      setIsDraggingFiles(false);
    }
  };

  // Handle drag state change from DragAndDropHandler
  const handleDragStateChange = (dragOver: boolean) => {
    setIsDragOver(dragOver);
    // Reset file detection when drag ends
    if (!dragOver) {
      setIsDraggingFiles(false);
    }
  };

  // Handle drop event from DragAndDropHandler
  const handleDrop = async (e: React.DragEvent) => {
    // Only handle if not in preview mode, has permissions, and not currently uploading
    if (previewDocument || uploading || !hasUpdatePermission) {
      return;
    }
    
    // Check if files are being dropped
    if (!e.dataTransfer.types.includes('Files')) {
      return;
    }
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const fileArray = Array.from(files);
      await processUploadFiles(fileArray);
    }
  };

  const handleDocumentClick = async (doc: Document) => {
    if (doc.type === 'system') {
      if (doc.source === 'cab_markdown' && projectId) {
        try {
          const content = await getCABMarkdownContent(projectId);
          setPreviewDocument({ ...doc, content });
        } catch {
          console.error('Failed to load CAB.md content');
          setPreviewDocument(doc);
        }
      } else {
        setPreviewDocument(doc);
      }
    } else if (onDocumentSelect) {
      onDocumentSelect(doc);
    }
  };

  const handleClosePreview = () => {
    setPreviewDocument(null);
  };

  const handleOpenInNewTab = (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    const artifactId = doc.id;
    const url = `/project/${projectId}/artifact/${artifactId}`;
    window.open(url, '_blank');
  };

  const handleDownload = async (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      if (doc.source === 'cab_markdown' && projectId) {
        await downloadCABMarkdown(projectId);
        return;
      }

      let response;
      
      if (doc.source === 'artifact') {
        // Download system-generated artifact
        response = await api.post(`/projects/${projectId}/artifacts/download-with-temp`, {
          artifactId: doc.id,
          messageId: `artifact_${doc.id}`,
          title: doc.name,
          content: doc.content || '',
          type: doc.fileType
        }, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        });
      } else if (doc.source === 'file') {
        // Download user-uploaded file
        response = await api.get(`/projects/${projectId}/files/${doc.id}/download`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        });
      } else if (doc.source === 'image') {
        // Download user-uploaded image
        response = await api.get(`/projects/${projectId}/images/${doc.id}/download`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        });
      }
      
      const blob = new Blob([response?.data || ''], { type: response?.headers['content-type'] || '' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const handleDelete = async (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!confirm(`Are you sure you want to delete "${doc.name}"?`)) {
      return;
    }

    try {
        if (doc.type === 'system') {
        await api.delete(`/projects/${projectId}/artifacts/${doc.id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        } else if (doc.source === 'file') {
        await api.delete(`/projects/${projectId}/files/${doc.id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      } else if (doc.source === 'image') {
        await api.delete(`/projects/${projectId}/images/${doc.id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      }
      
      // Reload documents
      loadDocuments();
    } catch (error) {
      console.error('Failed to delete document:', error);
      alert('Failed to delete file. Please try again.');
    }
  };

  const toggleFilter = (filter: Exclude<FileTypeFilter, 'all'>) => {
    const newFilters = new Set(selectedFilters);
    if (newFilters.has(filter)) {
      newFilters.delete(filter);
    } else {
      newFilters.add(filter);
    }
    setSelectedFilters(newFilters);
  };


  const isDragEnabled = !previewDocument && !uploading;

  return (
    <DragAndDropHandler
      disabled={!isDragEnabled}
      onDragEnter={handleDragEnter}
      onDrop={handleDrop}
      onDragStateChange={handleDragStateChange}
      useDragCounter={true}
      showOverlay={isDragOver && isDraggingFiles}
      className="relative flex flex-col h-full w-full bg-bg-primary rounded-bg-v2-lg container-shadow-lg overflow-hidden transition-all duration-200"
    >
      {/* Header - Only show when NOT in preview mode */}
      {!previewDocument && (
        <div className="flex items-center justify-between min-h-[60px] max-h-[60px] px-bg-v2-xl bg-bg-primary border-b border-border-secondary">
          <div className="flex items-center gap-bg-v2-lg flex-1">
            <Icon name={IconName.Layout} size="sm" variant="secondary" />
            <Text variant="text-sm" weight="semibold" color="primary">
              Files
            </Text>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-6 h-6 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200 flex-shrink-0"
            aria-label="Close"
          >
            <Icon name={IconName.Cross} size="sm" inheritColor />
          </button>
        </div>
      )}

      {/* Tabs - Only show when not in preview mode */}
      {!previewDocument && (
        <div className="flex flex-col items-start px-bg-v2-xl bg-bg-primary border-b border-border-secondary">
          <div className="flex items-center justify-center rounded-bg-v2-sm">
            <button
              className={`flex items-center justify-center px-bg-v2-lg py-bg-v2-lg bg-transparent border-0 border-b-2 ${activeTab === 'all' ? 'border-b-utility-brand-600' : 'border-b-transparent'} cursor-pointer transition-all duration-200`}
              onClick={() => setActiveTab('all')}
            >
              <Text variant="text-sm" weight={activeTab === 'all' ? 'semibold' : 'medium'} color={activeTab === 'all' ? 'primary' : 'tertiary'}>
                All
              </Text>
            </button>
            <button
              className={`flex items-center justify-center px-bg-v2-lg py-bg-v2-lg bg-transparent border-0 border-b-2 ${activeTab === 'user' ? 'border-b-utility-brand-600' : 'border-b-transparent'} cursor-pointer transition-all duration-200`}
              onClick={() => setActiveTab('user')}
            >
              <Text variant="text-sm" weight={activeTab === 'user' ? 'semibold' : 'medium'} color={activeTab === 'user' ? 'primary' : 'tertiary'}>
                User Uploaded
              </Text>
            </button>
            <button
              className={`flex items-center justify-center px-bg-v2-lg py-bg-v2-lg bg-transparent border-0 border-b-2 ${activeTab === 'system' ? 'border-b-utility-brand-600' : 'border-b-transparent'} cursor-pointer transition-all duration-200`}
              onClick={() => setActiveTab('system')}
            >
              <Text variant="text-sm" weight={activeTab === 'system' ? 'semibold' : 'medium'} color={activeTab === 'system' ? 'primary' : 'tertiary'}>
                System Generated
              </Text>
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1 flex-col items-start overflow-hidden bg-bg-primary">
        {previewDocument ? (
          previewDocument.source === 'cab_markdown' ? (
            /* CAB Markdown Preview */
            <div className="flex flex-col h-full w-full bg-bg-primary border-l border-border-primary">
              <div className="flex items-center justify-between px-bg-v2-lg py-bg-v2-lg bg-bg-primary border-b border-border-primary h-[60px]">
                <div className="flex items-center gap-3">
                  <Icon name={IconName.Document} size="md" className="text-fg-secondary" inheritColor />
                  <h2 className="text-sm font-semibold text-text-primary">CAB.md</h2>
                </div>
                <div className="flex items-center gap-0">
                  <button
                    onClick={(e) => handleDownload(previewDocument, e)}
                    className="w-8 h-8 flex items-center justify-center rounded-full text-fg-secondary hover:bg-bg-primary-hover transition-colors"
                    title="Download"
                  >
                    <Icon name={IconName.Download} size="md" inheritColor />
                  </button>
                  <button
                    onClick={handleClosePreview}
                    className="w-8 h-8 flex items-center justify-center rounded-full text-fg-secondary hover:bg-bg-primary-hover transition-all duration-200"
                    title="Close"
                  >
                    <Icon name={IconName.Cross} size="md" inheritColor />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                <div className="prose prose-lg max-w-none p-6">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children, ...props }: any) => (
                        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
                      ),
                    }}
                  >
                    {previewDocument.content || ''}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          ) : (
            /* Document Preview Content using ArtifactDetail */
            <ArtifactDetail
              artifact={{
                identifier: previewDocument.id,
                type: previewDocument.fileType === 'md' ? 'text/markdown' : previewDocument.fileType,
                title: previewDocument.name,
                content: previewDocument.content || '',
                version: previewDocument.version,
                id: previewDocument.id,
              } as any}
              isOpen={true}
              onClose={handleClosePreview}
            />
          )
        ) : (
          /* Documents List */
          <div className="flex flex-1 flex-col items-start p-bg-v2-xl w-full overflow-hidden">
            {/* Documents Table */}
            <div className="flex flex-col items-start w-full overflow-hidden gap-bg-v2-lg flex-1 min-h-0">
            <div className="flex flex-col w-full bg-bg-primary border border-border-secondary rounded-bg-v2-md container-shadow-sm overflow-hidden flex-1 min-h-0">
              {/* Search and Upload Row */}
              <div className="flex items-center justify-between w-full px-bg-v2-lg py-bg-v2-lg gap-bg-v2-lg border-b border-border-secondary">
                <div className="flex-shrink-0">
                  <Input
                    id="search-documents"
                    name="search-documents"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    startIcon={<Icon name={IconName.Search} size="sm" className="text-text-tertiary" />}
                    className="w-full !rounded-full"
                  />
                </div>
                <IfHasPermission
                  permissions={project?.permissions}
                  requires={Permissions.UpdateProjectPermission}
                >
                  <Button
                    variant="outline"
                    size="medium"
                    label={
                      <div className="flex items-center gap-1">
                        <Icon name={IconName.Plus} size="sm" inheritColor />
                        <span>Upload Files</span>
                      </div>
                    }
                    onClick={handleUploadFiles}
                    disabled={uploading}
                    className="flex-shrink-0"
                  />
                </IfHasPermission>
              </div>

          {/* File Type Filters */}
          <div className="flex flex-col gap-bg-v2-lg items-start p-bg-v2-lg border-b border-border-secondary w-full">
            <div className="flex gap-bg-v2-md items-center flex-wrap">
              <button
                className={`flex gap-bg-v2-md items-center py-bg-v2-sm px-bg-v2-lg rounded-full border cursor-pointer transition-all duration-200 ${selectedFilters.has('word') ? 'bg-utility-brand-50 border-utility-brand-200' : 'bg-bg-primary border-border-secondary hover:bg-bg-secondary'}`}
                onClick={() => toggleFilter('word')}
              >
                <FileTypeIcon extension="docx" size={16} />
                <Text variant="text-sm" weight="medium" color="quaternary">Word</Text>
              </button>
              <button
                className={`flex gap-bg-v2-md items-center py-bg-v2-sm px-bg-v2-lg rounded-full border cursor-pointer transition-all duration-200 ${selectedFilters.has('excel') ? 'bg-utility-brand-50 border-utility-brand-200' : 'bg-bg-primary border-border-secondary hover:bg-bg-secondary'}`}
                onClick={() => toggleFilter('excel')}
              >
                <FileTypeIcon extension="xlsx" size={16} />
                <Text variant="text-sm" weight="medium" color="quaternary">Excel</Text>
              </button>
              <button
                className={`flex gap-bg-v2-md items-center py-bg-v2-sm px-bg-v2-lg rounded-full border cursor-pointer transition-all duration-200 ${selectedFilters.has('powerpoint') ? 'bg-utility-brand-50 border-utility-brand-200' : 'bg-bg-primary border-border-secondary hover:bg-bg-secondary'}`}
                onClick={() => toggleFilter('powerpoint')}
              >
                <FileTypeIcon extension="pptx" size={16} />
                <Text variant="text-sm" weight="medium" color="quaternary">PowerPoint</Text>
              </button>
              <button
                className={`flex gap-bg-v2-md items-center py-bg-v2-sm px-bg-v2-lg rounded-full border cursor-pointer transition-all duration-200 ${selectedFilters.has('pdf') ? 'bg-utility-brand-50 border-utility-brand-200' : 'bg-bg-primary border-border-secondary hover:bg-bg-secondary'}`}
                onClick={() => toggleFilter('pdf')}
              >
                <FileTypeIcon extension="pdf" size={16} />
                <Text variant="text-sm" weight="medium" color="quaternary">PDF</Text>
              </button>
              <button
                className={`flex gap-bg-v2-md items-center py-bg-v2-sm px-bg-v2-lg rounded-full border cursor-pointer transition-all duration-200 ${selectedFilters.has('images') ? 'bg-utility-brand-50 border-utility-brand-200' : 'bg-bg-primary border-border-secondary hover:bg-bg-secondary'}`}
                onClick={() => toggleFilter('images')}
              >
                <FileTypeIcon extension="png" size={16} />
                <Text variant="text-sm" weight="medium" color="quaternary">Images</Text>
              </button>
              <button
                className={`flex gap-bg-v2-md items-center py-bg-v2-sm px-bg-v2-lg rounded-full border cursor-pointer transition-all duration-200 ${selectedFilters.has('others') ? 'bg-utility-brand-50 border-utility-brand-200' : 'bg-bg-primary border-border-secondary hover:bg-bg-secondary'}`}
                onClick={() => toggleFilter('others')}
              >
                <Text variant="text-sm" weight="medium" color="quaternary">Others</Text>
              </button>
            </div>
            <Text variant="text-sm" color="primary">
              Total {filteredDocuments.length} {filteredDocuments.length === 1 ? 'file' : 'files'}
            </Text>
          </div>

              {/* Upload Progress Indicator */}
              {uploading && (
                <div className="flex flex-col gap-bg-v2-md p-bg-v2-lg bg-utility-brand-50 border-b border-utility-brand-200 w-full animate-slide-in-down">
                  <div className="flex items-center gap-bg-v2-md">
                    <div className="w-4 h-4 border-2 border-utility-brand-200 border-t-utility-brand-600 rounded-full animate-rotate"></div>
                    <Text variant="text-sm" weight="medium" color="primary">
                      Uploading files... {uploadProgress.current} of {uploadProgress.total}
                    </Text>
                  </div>
                  <div className="w-full h-1.5 bg-utility-brand-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-utility-brand-600 rounded-full transition-smooth"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Table Container */}
              <div className="flex-1 overflow-auto min-h-0 modern-scrollbar">
                {!loading && filteredDocuments.length === 0 ? (
                  renderNoDocumentsFound()
                ) : (
                  // Loading or documents list - use existing table structure
                  <table className="w-full table-fixed">
                    {renderTableHeader("sticky top-0 bg-bg-primary z-10")}
                    <tbody>
                      {loading ? (
                        <>
                          {[0, 1, 2, 3, 4].map((index) => (
                            <DocumentRowSkeleton key={index} index={index} />
                          ))}
                        </>
                      ) : (
                        filteredDocuments.map((doc, index) => (
                          <tr
                            key={doc.id}
                            className="border-b border-border-secondary bg-bg-primary hover:bg-bg-secondary transition-colors cursor-pointer animate-fade-in-up opacity-0"
                            onClick={() => handleDocumentClick(doc)}
                            style={{
                              animationDelay: `${index * 0.05}s`,
                              animationFillMode: 'forwards'
                            }}
                          >
                            {/* Name Column */}
                            <td className="py-bg-v2-md px-bg-v2-lg">
                              <div className="flex items-center gap-bg-v2-md">
                                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                                  <FileTypeIcon extension={doc.fileType} size={32} />
                                </div>
                                <div className="flex flex-col min-w-0 flex-1">
                                  <Tooltip content={formatArtifactTitle(doc.name, doc.version)} position="top">
                                    <Text variant="text-sm" color="primary" className="truncate">{formatArtifactTitle(doc.name, doc.version)}</Text>
                                  </Tooltip>
                                  <Text variant="text-xs" color="quaternary">{formatFileSize(doc.size)}</Text>
                                </div>
                              </div>
                            </td>

                            {/* Date Column */}
                            <td className="py-bg-v2-md px-bg-v2-lg">
                              <div className="flex items-center justify-center">
                                <Text variant="text-sm" color="quaternary">{formatDate(doc.dateModified)}</Text>
                              </div>
                            </td>

                            {/* Type Column */}
                            <td className="py-bg-v2-md px-bg-v2-lg">
                              <div className="flex items-center justify-center">
                                <div className={`inline-flex items-center px-bg-v2-md py-bg-v2-xxs rounded-bg-v2-full border ${doc.type === 'user' ? 'bg-utility-brand-50 border-utility-brand-200 text-utility-brand-700' : 'bg-utility-orange-50 border-utility-orange-200 text-utility-orange-700'}`}>
                                  <Text variant="text-xs" weight="medium" className="capitalize">{doc.type}</Text>
                                </div>
                              </div>
                            </td>

                            {/* Action Column */}
                            <td className="py-bg-v2-md px-bg-v2-lg">
                              <div className="flex items-center justify-center gap-bg-v2-md">
                                {doc.source === 'cab_markdown' ? (
                                  // CAB.md: Download only (no delete, no open-in-new-tab)
                                  <button
                                    className="flex items-center justify-center bg-transparent border-0 cursor-pointer p-1 rounded-bg-v2-sm transition-all duration-200 text-text-tertiary hover:bg-utility-success-50 hover:text-utility-success-600"
                                    onClick={(e) => handleDownload(doc, e)}
                                    title="Download"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M4.66602 6.66667L7.99935 10L11.3327 6.66667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M8 10V2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                ) : doc.type === 'system' ? (
                                  // System documents: Open in New Tab, Download, Delete
                                  <>
                                    <button
                                      className="flex items-center justify-center bg-transparent border-0 cursor-pointer p-1 rounded-bg-v2-sm transition-all duration-200 text-text-tertiary hover:bg-utility-brand-50 hover:text-utility-brand-600"
                                      onClick={(e) => handleOpenInNewTab(doc, e)}
                                      title="Open in New Tab"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M13.3333 8.66667V12.6667C13.3333 13.0203 13.1929 13.3594 12.9428 13.6095C12.6928 13.8595 12.3536 14 12 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V4C2 3.64638 2.14048 3.30724 2.39052 3.05719C2.64057 2.80714 2.97971 2.66667 3.33333 2.66667H7.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M10.6667 2H14.0001V5.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M6.66602 9.33333L13.9993 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </button>
                                    <button
                                      className="flex items-center justify-center bg-transparent border-0 cursor-pointer p-1 rounded-bg-v2-sm transition-all duration-200 text-text-tertiary hover:bg-utility-success-50 hover:text-utility-success-600"
                                      onClick={(e) => handleDownload(doc, e)}
                                      title="Download"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M4.66602 6.66667L7.99935 10L11.3327 6.66667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M8 10V2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </button>
                                    <IfHasPermission
                                      permissions={project?.permissions}
                                      requires={Permissions.UpdateProjectPermission}
                                    >
                                      <button
                                        className="flex items-center justify-center bg-transparent border-0 cursor-pointer p-1 rounded-bg-v2-sm transition-all duration-200 text-text-tertiary hover:bg-utility-error-50 hover:text-utility-error-600"
                                        onClick={(e) => handleDelete(doc, e)}
                                        title="Delete"
                                      >
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                          <path d="M5.33398 4.00004V2.66671C5.33398 2.31309 5.47446 1.97395 5.72451 1.7239C5.97456 1.47385 6.3137 1.33337 6.66732 1.33337H9.33398C9.68761 1.33337 10.0267 1.47385 10.2768 1.7239C10.5268 1.97395 10.6673 2.31309 10.6673 2.66671V4.00004M12.6673 4.00004V13.3334C12.6673 13.687 12.5268 14.0261 12.2768 14.2762C12.0267 14.5262 11.6876 14.6667 11.334 14.6667H4.66732C4.3137 14.6667 3.97456 14.5262 3.72451 14.2762C3.47446 14.0261 3.33398 13.687 3.33398 13.3334V4.00004H12.6673Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      </button>
                                    </IfHasPermission>
                                  </>
                                ) : (
                                  // User documents: Download, Delete
                                  <>
                                    <button
                                      className="flex items-center justify-center bg-transparent border-0 cursor-pointer p-1 rounded-bg-v2-sm transition-all duration-200 text-text-tertiary hover:bg-utility-success-50 hover:text-utility-success-600"
                                      onClick={(e) => handleDownload(doc, e)}
                                      title="Download"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M4.66602 6.66667L7.99935 10L11.3327 6.66667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M8 10V2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </button>
                                    <IfHasPermission
                                      permissions={project?.permissions}
                                      requires={Permissions.UpdateProjectPermission}
                                    >
                                      <button
                                        className="flex items-center justify-center bg-transparent border-0 cursor-pointer p-1 rounded-bg-v2-sm transition-all duration-200 text-text-tertiary hover:bg-utility-error-50 hover:text-utility-error-600"
                                        onClick={(e) => handleDelete(doc, e)}
                                        title="Delete"
                                      >
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                          <path d="M5.33398 4.00004V2.66671C5.33398 2.31309 5.47446 1.97395 5.72451 1.7239C5.97456 1.47385 6.3137 1.33337 6.66732 1.33337H9.33398C9.68761 1.33337 10.0267 1.47385 10.2768 1.7239C10.5268 1.97395 10.6673 2.31309 10.6673 2.66671V4.00004M12.6673 4.00004V13.3334C12.6673 13.687 12.5268 14.0261 12.2768 14.2762C12.0267 14.5262 11.6876 14.6667 11.334 14.6667H4.66732C4.3137 14.6667 3.97456 14.5262 3.72451 14.2762C3.47446 14.0261 3.33398 13.687 3.33398 13.3334V4.00004H12.6673Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      </button>
                                    </IfHasPermission>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
          </div>
        )}
      </div>
    </DragAndDropHandler>
  );
};

export default DocumentsPanel;

